/**
 * In-memory patches to GET `/tasks` snapshot JSON so we can avoid redundant GET calls after
 * `PUT …/tasks/complete` for subtasks (and optional parent incomplete) while keeping session storage aligned.
 *
 * Uses a **deep** walk so subtasks nested under parent rows (not only a flat relay list) still match.
 */

function taskRowType(row: Record<string, unknown>): 'subtask' | 'task' | 'unknown' {
  const t = String(row.type ?? '').trim().toLowerCase();
  if (t === 'subtask') return 'subtask';
  if (t === 'task') return 'task';
  return 'unknown';
}

function rowNumericId(row: Record<string, unknown>): number | undefined {
  const raw = row.taskId ?? row.id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function deepPatchSubtaskCompletion(root: unknown, subTaskId: number, completed: boolean): boolean {
  if (root === null || typeof root !== 'object') return false;
  if (Array.isArray(root)) {
    for (const el of root) {
      if (deepPatchSubtaskCompletion(el, subTaskId, completed)) return true;
    }
    return false;
  }
  const row = root as Record<string, unknown>;
  if (taskRowType(row) === 'subtask' && rowNumericId(row) === subTaskId) {
    row.completed = completed;
    return true;
  }
  for (const v of Object.values(row)) {
    if (deepPatchSubtaskCompletion(v, subTaskId, completed)) return true;
  }
  return false;
}

/** Parent / non-subtask row with matching relay `taskId` (flat list or nested under wrappers). */
function deepPatchParentTaskCompletion(
  root: unknown,
  parentTaskId: number,
  completed: boolean
): boolean {
  if (root === null || typeof root !== 'object') return false;
  if (Array.isArray(root)) {
    for (const el of root) {
      if (deepPatchParentTaskCompletion(el, parentTaskId, completed)) return true;
    }
    return false;
  }
  const row = root as Record<string, unknown>;
  if (taskRowType(row) === 'subtask') {
    for (const v of Object.values(row)) {
      if (deepPatchParentTaskCompletion(v, parentTaskId, completed)) return true;
    }
    return false;
  }
  const id = rowNumericId(row);
  if (id === parentTaskId) {
    row.completed = completed;
    return true;
  }
  for (const v of Object.values(row)) {
    if (deepPatchParentTaskCompletion(v, parentTaskId, completed)) return true;
  }
  return false;
}

export type LocalTasksSnapshotPatch = {
  subtask?: { taskId: number; completed: boolean };
  /** Parent `type: task` (or non-subtask) row — e.g. mark parent incomplete when unchecking last subtask. */
  parentTask?: { taskId: number; completed: boolean };
};

/**
 * Deep-clones `data` and applies completion patches anywhere in the JSON tree.
 * Returns `null` if a requested patch did not match any row.
 */
export function cloneTasksPayloadWithLocalCompletionPatches(
  data: unknown,
  patch: LocalTasksSnapshotPatch
): unknown | null {
  if (data == null) return null;
  try {
    const root = JSON.parse(JSON.stringify(data)) as unknown;

    let subOk = !patch.subtask;
    let parOk = !patch.parentTask;

    if (patch.subtask) {
      subOk = deepPatchSubtaskCompletion(root, patch.subtask.taskId, patch.subtask.completed);
    }
    if (patch.parentTask) {
      parOk = deepPatchParentTaskCompletion(root, patch.parentTask.taskId, patch.parentTask.completed);
    }

    if (!subOk || !parOk) return null;
    return root;
  } catch {
    return null;
  }
}
